(function () {
  'use strict';

  if (window.__quoteRequestScriptLoaded) return;
  window.__quoteRequestScriptLoaded = true;

  const CURRENCY_FORMATTER = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD',
  });

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    try {
      return CURRENCY_FORMATTER.format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  function visibleProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};

    return Object.entries(properties).reduce((visible, [key, value]) => {
      if (!key || key.charAt(0) === '_' || value === null || value === undefined || value === '') return visible;
      visible[key] = value;
      return visible;
    }, {});
  }

  function buildQuoteBody(cart, form) {
    const lines = [];

    lines.push('Quote request');
    lines.push('');
    lines.push('Requested items:');

    cart.items.forEach((item, index) => {
      lines.push('');
      lines.push(`${index + 1}. ${item.product_title}`);
      if (item.variant_title && item.variant_title !== 'Default Title') lines.push(`Variant: ${item.variant_title}`);
      if (item.sku) lines.push(`SKU: ${item.sku}`);
      lines.push(`Variant ID: ${item.variant_id}`);
      lines.push(`Quantity: ${item.quantity}`);
      lines.push(`Unit price: ${formatMoney(item.final_price)}`);
      if (item.url) lines.push(`URL: ${window.location.origin}${item.url}`);

      const properties = visibleProperties(item.properties);
      const propertyEntries = Object.entries(properties);
      if (propertyEntries.length) {
        lines.push('Line item properties:');
        propertyEntries.forEach(([key, value]) => {
          lines.push(`- ${key}: ${value}`);
        });
      }
    });

    lines.push('');
    lines.push(`Estimated total: ${formatMoney(cart.total_price)}`);

    return lines.join('\n');
  }

  function buildPayload(cart, form) {
    return {
      contact: {
        name: form.elements['contact[name]']?.value?.trim() || '',
        email: form.elements['contact[email]']?.value?.trim() || '',
        phone: form.elements['contact[phone]']?.value?.trim() || '',
        company: form.elements['contact[company]']?.value?.trim() || '',
        message: form.elements['contact[message]']?.value?.trim() || '',
      },
      cart: {
        token: cart.token || '',
        total_price: cart.total_price,
        currency: window.Shopify?.currency?.active || '',
        items: cart.items.map((item) => ({
          key: item.key,
          variant_id: item.variant_id,
          product_id: item.product_id,
          product_title: item.product_title,
          variant_title: item.variant_title,
          sku: item.sku,
          quantity: item.quantity,
          price: item.price,
          final_price: item.final_price,
          url: item.url,
          properties: visibleProperties(item.properties),
        })),
      },
      context: {
        shop: window.Shopify?.shop || window.location.hostname,
        page_url: window.location.href,
        currency: window.Shopify?.currency?.active || '',
      },
    };
  }

  function setStatus(container, message, isError) {
    if (!container) return;
    container.textContent = message;
    container.hidden = false;
    container.classList.toggle('quote-request__status--error', Boolean(isError));
    container.classList.toggle('quote-request__status--success', !isError);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  async function clearCartIfNeeded(shouldClearCart) {
    if (!shouldClearCart) return;

    const response = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error('Unable to clear cart');
  }

  function submitNativeContactForm(form) {
    try {
      window.sessionStorage.setItem('quoteRequestSubmitted', 'true');
    } catch (error) {
      // Storage can be unavailable in private contexts; native submit still works.
    }

    form.dataset.quoteNativeFallback = 'true';
    HTMLFormElement.prototype.submit.call(form);
  }

  function showSuccess(component, form, status, message) {
    const summary = component?.querySelector('[data-quote-request-summary]');
    const formCard = component?.querySelector('[data-quote-request-form-card]');
    const controls = component?.querySelector('[data-quote-request-controls]');
    const success = component?.querySelector('[data-quote-request-success]');

    form.reset();
    if (controls instanceof HTMLElement) controls.hidden = true;
    if (status instanceof HTMLElement) {
      status.textContent = message;
      status.hidden = true;
      status.classList.remove('quote-request__status--error');
      status.classList.add('quote-request__status--success');
    }
    if (formCard instanceof HTMLElement) formCard.hidden = false;
    if (summary instanceof HTMLElement) summary.hidden = true;
    if (success instanceof HTMLElement) {
      success.hidden = false;
      success.focus({ preventScroll: true });
    }
  }

  async function handleSubmit(event) {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.quoteNativeFallback === 'true') return;

    const component = form.closest('quote-request-component');
    const status = component?.querySelector('[data-quote-request-status]');
    const bodyField = form.querySelector('[data-quote-request-body]');
    const endpoint = component?.getAttribute('data-processor-endpoint')?.trim();
    const clearCart = component?.getAttribute('data-clear-cart-after-submit') === 'true';

    if (!form.checkValidity()) return;

    event.preventDefault();

    try {
      setStatus(status, 'Preparing your quote request...', false);
      const cart = await fetchCart();
      const body = buildQuoteBody(cart, form);
      const payload = buildPayload(cart, form);

      if (bodyField) bodyField.value = body;

      if (!endpoint) {
        setStatus(status, 'Submitting your quote request...', false);
        await clearCartIfNeeded(clearCart);
        submitNativeContactForm(form);
        return;
      }

      setStatus(status, 'Submitting your quote request...', false);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = {};
      }

      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Quote processor unavailable');
      }

      await clearCartIfNeeded(clearCart);

      const quoteId = data.quote_id || data.draft_order_name || data.name || '';
      showSuccess(
        component,
        form,
        status,
        quoteId ? `Quote request submitted. Reference: ${quoteId}` : 'Quote request submitted.'
      );
    } catch (error) {
      if (endpoint) {
        try {
          setStatus(status, 'The quote processor is unavailable, so we are sending this request by email instead.', true);
          await clearCartIfNeeded(clearCart);
          submitNativeContactForm(form);
          return;
        } catch (fallbackError) {
          // Fall through to the shared error below.
        }
      }

      setStatus(status, 'We could not submit this quote request. Please try again.', true);
    }
  }

  function init() {
    document.querySelectorAll('[data-quote-request-form]').forEach((form) => {
      form.addEventListener('submit', handleSubmit);

      let showSubmittedState = false;
      try {
        showSubmittedState = window.sessionStorage.getItem('quoteRequestSubmitted') === 'true';
        if (showSubmittedState) window.sessionStorage.removeItem('quoteRequestSubmitted');
      } catch (error) {
        showSubmittedState = false;
      }

      if (showSubmittedState) {
        const component = form.closest('quote-request-component');
        const status = component?.querySelector('[data-quote-request-status]');
        showSuccess(component, form, status, 'Quote request submitted. We will follow up with pricing and next steps.');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
